export function landingFooterPopover() {
    return {
        open: false,
        moving: false,
        active: null,
        title: '',
        description: '',
        left: 0,
        width: 248,
        arrow: 24,
        hideTimer: null,
        showHint(target) {
            const trigger = target?.closest('[data-footer-hint]');
            if (!trigger) { return; }
            clearTimeout(this.hideTimer);
            this.moving = this.open;
            const bounds = trigger.getBoundingClientRect();
            const group = this.$refs.footerIconGroup.getBoundingClientRect();
            this.width = Math.min(248, window.innerWidth - 32);
            const center = bounds.left + bounds.width / 2;
            const anchor = group.left + group.width / 2 < window.innerWidth / 2 ? 40 : this.width - 40;
            const viewportLeft = Math.max(16, Math.min(center - anchor, window.innerWidth - this.width - 16));
            this.left = viewportLeft - group.left;
            this.arrow = Math.max(16, Math.min(this.width - 16, center - viewportLeft));
            this.title = trigger.dataset.footerTitle;
            this.description = trigger.dataset.footerDescription;
            this.active = trigger;
            this.open = true;
        },
        queueHide() {
            clearTimeout(this.hideTimer);
            this.hideTimer = setTimeout(() => {
                const focused = document.activeElement?.closest('[data-footer-hint]');
                if (focused?.matches(':focus-visible') && this.$refs.footerIconGroup.contains(focused)) {
                    this.showHint(focused);
                } else {
                    this.closeHint();
                }
            }, 140);
        },
        closeHint() {
            clearTimeout(this.hideTimer);
            this.open = false;
            this.active = null;
        },
        repositionHint() {
            if (this.open && this.active) { this.showHint(this.active); }
        },
        destroy() { clearTimeout(this.hideTimer); },
    };
}

export function landingStats() {
    return {
        shown: false,
        visible: false,
        pageVisible: !document.hidden,
        bots: 0,
        uptime: 0,
        hours: 0,
        now: new Date(),
        ticking: false,
        clockTimer: null,
        clockOrigin: null,
        observer: null,
        timers: new Set(),
        reduced: window.matchMedia('(prefers-reduced-motion: reduce)').matches,
        moveStatIcons(event) {
            if (event.pointerType !== 'mouse' || !window.matchMedia('(hover: hover) and (pointer: fine) and (prefers-reduced-motion: no-preference)').matches) { return; }
            const card = event.currentTarget;
            const bounds = card.getBoundingClientRect();
            const x = Math.max(-1, Math.min(1, (event.clientX - bounds.left) / bounds.width * 2 - 1));
            const y = Math.max(-1, Math.min(1, (event.clientY - bounds.top) / bounds.height * 2 - 1));
            card.style.setProperty('--stat-pointer-x', `${(x * 6).toFixed(2)}px`);
            card.style.setProperty('--stat-pointer-y', `${(y * 4).toFixed(2)}px`);
        },
        resetStatIcons(card) {
            card.style.setProperty('--stat-pointer-x', '0px');
            card.style.setProperty('--stat-pointer-y', '0px');
        },
        countUp(target, set, duration = 1400) {
            if (this.reduced || !this.visible || !this.pageVisible) { set(target); return; }
            const start = performance.now();
            const step = (now) => {
                const progress = Math.min((now - start) / duration, 1);
                if (!this.visible || !this.pageVisible) { set(target); return; }
                set(target * (1 - Math.pow(1 - progress, 4)));
                if (progress < 1) { requestAnimationFrame(step); }
            };
            requestAnimationFrame(step);
        },
        schedule(callback, delay) {
            const timer = setTimeout(() => { this.timers.delete(timer); callback(); }, delay);
            this.timers.add(timer);
        },
        localSeconds(date) { return date.getTime() / 1000 - date.getTimezoneOffset() * 60; },
        handAngle(hand) {
            if (this.clockOrigin === null) { return 0; }
            const origin = this.clockOrigin;
            const current = this.localSeconds(this.now);
            const start = { hour: (origin / 120) % 360, minute: (origin / 10) % 360, second: (Math.floor(origin) * 6) % 360 }[hand];
            const growth = { hour: (current - origin) / 120, minute: (current - origin) / 10, second: (Math.floor(current) - Math.floor(origin)) * 6 }[hand];
            return start + growth;
        },
        clockText() { return this.now.toLocaleTimeString('fa-IR', { hour: '2-digit', minute: '2-digit' }); },
        startClock() {
            this.stopClock();
            if (!this.ticking || !this.visible || !this.pageVisible) { return; }
            this.now = new Date();
            this.clockTimer = setInterval(() => { this.now = new Date(); }, 1000);
        },
        stopClock() { clearInterval(this.clockTimer); this.clockTimer = null; },
        syncPageVisibility() {
            this.pageVisible = !document.hidden;
            this.startClock();
        },
        init() {
            this.schedule(() => { this.shown = true; }, 3000);
            this.observer = new IntersectionObserver(([entry]) => {
                this.visible = entry.isIntersecting;
                this.startClock();
            });
            this.observer.observe(this.$el);
            this.$watch('shown', (value) => {
                if (!value) { return; }
                this.schedule(() => {
                    this.countUp(600, (number) => this.bots = Math.round(number));
                    this.countUp(99.99, (number) => this.uptime = number, 1600);
                    this.countUp(24, (number) => this.hours = Math.round(number), 1200);
                }, 350);
                const introLength = this.reduced ? 0 : 2800;
                this.clockOrigin = this.localSeconds(new Date());
                this.now = new Date(Date.now() + introLength);
                this.schedule(() => { this.ticking = true; this.startClock(); }, introLength);
            });
        },
        destroy() {
            this.visible = false;
            this.stopClock();
            this.observer?.disconnect();
            this.timers.forEach((timer) => clearTimeout(timer));
            this.timers.clear();
        },
    };
}

export function landingDashboard(highlightCount) {
    return {
        shown: false,
        active: 0,
        started: false,
        visible: false,
        pageVisible: !document.hidden,
        duration: 5500,
        timer: null,
        revealTimer: null,
        observer: null,
        reduced: window.matchMedia('(prefers-reduced-motion: reduce)').matches,
        init() {
            this.revealTimer = setTimeout(() => { this.shown = true; }, 3000);
            this.$watch('shown', (value) => {
                if (value && !this.started) { this.timer = setTimeout(() => { this.started = true; this.play(); }, 900); }
            });
            this.observer = new IntersectionObserver(([entry]) => {
                this.visible = entry.isIntersecting;
                if (this.started) { this.play(); }
            });
            this.observer.observe(this.$el);
        },
        play() {
            this.stop();
            if (!this.visible || !this.pageVisible) { return; }
            this.timer = setTimeout(() => { this.active = (this.active + 1) % highlightCount; this.play(); }, this.duration);
        },
        stop() { clearTimeout(this.timer); this.timer = null; },
        syncPageVisibility() {
            this.pageVisible = !document.hidden;
            if (this.started) { this.play(); }
        },
        countUp(target, set) {
            if (this.reduced || !this.visible || !this.pageVisible) { set(target); return; }
            const start = performance.now(), duration = 1100;
            const step = (now) => {
                const progress = Math.min((now - start) / duration, 1);
                if (!this.visible || !this.pageVisible) { set(target); return; }
                set(Math.round(target * (1 - Math.pow(1 - progress, 4))));
                if (progress < 1) { requestAnimationFrame(step); }
            };
            requestAnimationFrame(step);
        },
        fa(number) { return number.toLocaleString('fa-IR'); },
        select(index) { this.active = index; this.started = true; this.play(); },
        destroy() {
            this.visible = false;
            this.stop();
            clearTimeout(this.revealTimer);
            this.observer?.disconnect();
        },
    };
}
